// Knowledge base data on the screens: folders, a folder (looking again every two seconds while a file is being
// read), a file, and uploads that show their progress (the browser sends the bytes itself, then completes).
import { useCallback, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type FileItem, type FolderDetail, type NameConflict, type UploadTicket } from '@dpw/shared';
import { api, API_BASE } from '../api';

export const useFolders = (q: { spaceId?: string } = {}) => useQuery({ queryKey: ['folders', q.spaceId ?? null], queryFn: () => api.knowledge.folders(q) });
export const useFolder = (id: string) => useQuery({
  queryKey: ['folder', id], queryFn: () => api.knowledge.folder(id), enabled: !!id,
  refetchInterval: q => (q.state.data?.files.some(f => f.status === 'reading') ? 2000 : false),
});
/** watch: look again while something is being written (a rewrite, a restore). */
export const useFile = (id: string | null, watch = false) => useQuery({
  queryKey: ['file', id], queryFn: () => api.knowledge.file(id!), enabled: !!id,
  refetchInterval: q => (watch || q.state.data?.status === 'reading' ? 1500 : false),
});
export const useSearch = (q: string) => useQuery({ queryKey: ['kb-search', q], queryFn: () => api.knowledge.search(q).then(r => r.items), enabled: q.trim().length >= 2, staleTime: 15_000 });

/** Send the bytes for a ticket with progress: a form POST to S3, or a PUT to the API on local storage. */
function sendBytes(t: UploadTicket, file: File, onPct: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const x = new XMLHttpRequest();
    const local = t.method === 'PUT';
    x.open(t.method, local ? `${API_BASE}${t.url}` : t.url);
    if (local) { x.withCredentials = true; x.setRequestHeader('x-dpw-client', 'web'); x.setRequestHeader('content-type', 'application/octet-stream'); }
    x.upload.onprogress = e => { if (e.lengthComputable) onPct(Math.round((100 * e.loaded) / e.total)); };
    x.onload = () => (x.status < 300 ? resolve() : reject(new ApiError(x.status, 'upload_failed', 'The file could not be sent. Try again.')));
    x.onerror = () => reject(new ApiError(0, 'upload_failed', 'The file could not be sent. Check the connection and try again.'));
    if (local) x.send(file);
    else { const fd = new FormData(); for (const [k, v] of Object.entries(t.fields)) fd.append(k, v); fd.append('file', file); x.send(fd); }
  });
}

/** fileId: a new version of a file already in the folder, shown on that file's own row. */
/** The folder's order (by name, as the API lists them). */
const byName = (fs: FileItem[]) => fs.sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : a.name.toLowerCase() > b.name.toLowerCase() ? 1 : 0));

export interface Pending { key: string; name: string; pct: number; fileId?: string }
export interface Conflict extends NameConflict { file: File }

/**
 * Uploads into a folder: each file shows as a row with its progress, then the folder's own row (Reading..., then the
 * summary). A name already in the folder pauses as a conflict for the person to answer (add as version N, keep both).
 */
export function useUploads(folderId: string, opts: { onDone?: (f: FileItem, how: 'new' | 'version') => void; onError?: (e: unknown) => void } = {}) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<Pending[]>([]);
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [fresh, setFresh] = useState<string[]>([]);
  const seq = useRef(0);
  const o = useRef(opts);
  o.current = opts;
  const run = useCallback(async (file: File, resolve?: 'version' | 'keep_both') => {
    const key = `u${seq.current++}`;
    try {
      const t = await api.knowledge.startUpload(folderId, { name: file.name, size: file.size, mime: file.type, ...(resolve ? { resolve } : {}) });
      setPending(p => [{ key, name: t.name, pct: 2, ...(t.fileId ? { fileId: t.fileId } : {}) }, ...p]);
      await sendBytes(t, file, pct => setPending(p => p.map(x => (x.key === key ? { ...x, pct: Math.max(2, pct) } : x))));
      const done = await api.knowledge.completeUpload(t.uploadId);
      // The file's own row takes the place of the progress row in the same render (never both on screen): put it in
      // the folder now, then look again for the rest.
      qc.setQueryData<FolderDetail>(['folder', folderId], d => d && { ...d, files: byName([...d.files.filter(f => f.id !== done.id), done]) });
      setPending(p => p.filter(x => x.key !== key));
      setFresh(f => [...f, done.id]);
      await qc.invalidateQueries();
      o.current.onDone?.(done, t.fileId ? 'version' : 'new');
    } catch (e) {
      const c = e instanceof ApiError && e.code === 'name_conflict' ? e.conflict : undefined;
      if (c) setConflicts(cs => [...cs, { ...c, file }]);
      else o.current.onError?.(e);
    } finally {
      setPending(p => p.filter(x => x.key !== key));
    }
  }, [folderId, qc]);
  const add = useCallback((files: FileList | File[]) => { for (const f of Array.from(files)) void run(f); }, [run]);
  /** Answer the first conflict: add as the next version, keep both, or leave it. */
  const resolve = useCallback((how: 'version' | 'keep_both' | 'cancel') => {
    const c = conflicts[0];
    setConflicts(cs => cs.slice(1));
    if (c && how !== 'cancel') void run(c.file, how);
  }, [run, conflicts]);
  return { pending, conflict: conflicts[0] ?? null, fresh, add, resolve };
}

/** "Upload new version" on a file: the same flow, straight to the next version. */
export async function uploadVersion(fileId: string, file: File, onPct: (pct: number) => void = () => undefined): Promise<FileItem> {
  const t = await api.knowledge.startVersionUpload(fileId, { name: file.name, size: file.size, mime: file.type });
  await sendBytes(t, file, onPct);
  return api.knowledge.completeUpload(t.uploadId);
}
